// Customer flows – template text and follow-up steps for supporting paying customers
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

const FLOWS_COLLECTION = 'flows'

/**
 * Create a new flow
 * @param {Object} data - { name, description?, items? }
 * @returns {Promise<string>} Document ID
 */
export const createFlow = async (data) => {
  const docRef = await addDoc(collection(db, FLOWS_COLLECTION), {
    name: (data.name || '').trim(),
    description: (data.description || '').trim() || null,
    items: Array.isArray(data.items) ? data.items : [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  })
  return docRef.id
}

/**
 * Update a flow
 * @param {string} flowId - Flow document ID
 * @param {Object} updates - { name?, description?, items? }
 */
export const updateFlow = async (flowId, updates) => {
  const ref = doc(db, FLOWS_COLLECTION, flowId)
  const payload = { ...updates, updatedAt: serverTimestamp() }
  if (payload.name !== undefined) payload.name = (payload.name || '').trim()
  if (payload.description !== undefined) payload.description = (payload.description || '').trim() || null
  await updateDoc(ref, payload)
}

/**
 * Delete a flow
 * @param {string} flowId - Flow document ID
 */
export const deleteFlow = async (flowId) => {
  await deleteDoc(doc(db, FLOWS_COLLECTION, flowId))
}

/**
 * Get all flows
 * @returns {Promise<Array>} Array of flows
 */
export const getFlows = async () => {
  const q = query(
    collection(db, FLOWS_COLLECTION),
    orderBy('createdAt', 'desc')
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
}

/**
 * Subscribe to flows (real-time)
 * @param {Function} callback - (flows: Array) => void
 * @returns {Function} Unsubscribe
 */
export const subscribeToFlows = (callback) => {
  const q = query(
    collection(db, FLOWS_COLLECTION),
    orderBy('createdAt', 'desc')
  )
  return onSnapshot(q, (snapshot) => {
    const flows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
    callback(flows)
  }, (err) => {
    console.error('Flows subscription error:', err)
  })
}

/** Generate a simple id for flow items */
export const createItemId = () => `item_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
